import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Paper, Typography } from '@material-ui/core';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { monthsList } from '../../helpers/constants';
import '../../index.css';
import PortfolioFaultSummary from './PortfolioFaultSummary';

class PortfolioFaultHistogram extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartOptions: {
        chart: {
          type: 'column',
          backgroundColor: '#ffffff',
          marginRight: 50,
        },
        title: { text: '' },
        xAxis: {
          categories: [],
          crosshair: true
        },
        yAxis: {
          min: 0,
          title: { text: 'Number of Faults' },
          stackLabels: {
            enabled: true,
            style: { fontWeight: 'bold' }
          }
        },
        tooltip: {
          shared: true,
          useHTML: true,
          headerFormat: '<b>{point.key}</b><br/>',
          pointFormat: '{series.name}: {point.y}<br/>',
          footerFormat: '<b>Total: {point.total}</b>'
        },
        legend: {
          enabled: true,
          itemStyle: {
            cursor: 'default'
          }
        },
        plotOptions: {
          column: {
            stacking: 'normal',
            dataLabels: {
              enabled: true,
              formatter: function () {
                return this.y > 0 ? this.y : '';
              }
            }
          },
          series: {
            events: {
              legendItemClick: function () {
                return false;
              }
            }
          }
        },
        series: [],
        credits: { enabled: false },
        exporting: {
            enabled: true,
            buttons: {
            contextButton: {
                menuItems: ['viewFullscreen'] // only this item will show
            }
          }
        }
      },
      hasData: false
    };
  }

  componentDidMount() {
    this.processData();
  }

  componentDidUpdate(prevProps) {
    const propsChanged =
      prevProps.portfolioFaultData !== this.props.portfolioFaultData ||
      prevProps.portfolioFaultLogData !== this.props.portfolioFaultLogData ||
      prevProps.selectedDisplayMode !== this.props.selectedDisplayMode ||
      prevProps.selectedYear !== this.props.selectedYear ||
      prevProps.selectedMonth !== this.props.selectedMonth ||
      prevProps.selectedPortfolioFaultStations.length !== this.props.selectedPortfolioFaultStations.length;

    if (propsChanged) {
      this.processData();
    }
  }

  componentWillUnmount() {
    const chartContainer = document.querySelector('.portfolioFaultHistogram .highcharts-container');
    if (chartContainer) chartContainer.remove();
  }

  generateLabelKey = (timestamp) => {
    const { selectedDisplayMode } = this.props;
    if (selectedDisplayMode === 'year') return monthsList[timestamp.getMonth()].name;
    if (selectedDisplayMode === 'month') return `${timestamp.getDate()}`;
    return '';
  };

  generateXLabels = () => {
    const { selectedDisplayMode, selectedYear, selectedMonth } = this.props;
    if (selectedDisplayMode === 'year') return monthsList.map((m) => m.name);
    if (selectedDisplayMode === 'month') {
      const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());
    }
    return [];
  };

  clearChart = () => {
    this.setState({
      chartOptions: {
        ...this.state.chartOptions,
        xAxis: { ...this.state.chartOptions.xAxis, categories: [] },
        series: []
      },
      hasData: false
    });
  };

  processData = () => {
    const {
      portfolioFaultData,
      portfolioFaultLogData,
      portfolioFaultDetails,
      selectedDisplayMode,
      selectedPortfolioFaultStations
    } = this.props;

    const isSingleStationMode = selectedPortfolioFaultStations?.length === 1;
    const xLabels = this.generateXLabels();

    if (isSingleStationMode) {
      if (!portfolioFaultLogData || portfolioFaultLogData.length === 0) {
        this.clearChart();
        return;
      }

      const deviceMap = {};
      portfolioFaultLogData.forEach((fault) => {
        const timestamp = new Date(fault.timestamp * 1000);
        fault.deviceMaps?.forEach(device => {
          const deviceName = device.deviceName || 'Unknown Device';
          const labelKey = this.generateLabelKey(timestamp);
          deviceMap[deviceName] = deviceMap[deviceName] || {};
          deviceMap[deviceName][labelKey] = (deviceMap[deviceName][labelKey] || 0) + 1;
        });
      });

      const newSeries = Object.entries(deviceMap)
      .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
      .map(([name, dataMap]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1).toLowerCase(),
        data: xLabels.map(label => dataMap[label] || 0)
      }));

      this.setState({
        chartOptions: {
          ...this.state.chartOptions,
          xAxis: { ...this.state.chartOptions.xAxis, categories: xLabels },
          series: newSeries
        },
        hasData: newSeries.some(serie => serie.data.some(value => value > 0))
      });

    } else {
      if (!portfolioFaultData || portfolioFaultData.length === 0) {
        this.clearChart();
        return;
      }

      const countryMap = {};
      portfolioFaultData.forEach(fault => {
        const timestamp = new Date(fault.timestamp);
        const station = portfolioFaultDetails.stationDetails?.find((s) => s.name === fault.stationId);
        const country = station?.country || 'Unknown';
        const labelKey = this.generateLabelKey(timestamp);
        const faultCount = fault.faultTypes?.reduce((sum, f) => sum + (f.count || 0), 0) || 0;

        countryMap[country] = countryMap[country] || {};
        countryMap[country][labelKey] = (countryMap[country][labelKey] || 0) + faultCount;
      });

      const newSeries = Object.entries(countryMap).map(([name, dataMap]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1).toLowerCase(),
        data: xLabels.map(label => dataMap[label] || 0)
      }));

      const currentSeriesStr = JSON.stringify(this.state.chartOptions.series);
      const newSeriesStr = JSON.stringify(newSeries);
      if (currentSeriesStr === newSeriesStr) return;

      this.setState({
        chartOptions: {
          ...this.state.chartOptions,
          xAxis: { ...this.state.chartOptions.xAxis, categories: xLabels },
          series: newSeries
        },
        hasData: newSeries.some(serie => serie.data.some(value => value > 0))
      });
    }
  };

  render() {
    const { loading, selectedPortfolioFaultStations, selectedPortfolioFaultLevel, selectedYear, portfolioFaultDetails, portfolioFaultData } = this.props;
    const { hasData } = this.state;

    const portfolioLevelText = `${selectedPortfolioFaultLevel.charAt(0).toUpperCase()}${selectedPortfolioFaultLevel.slice(1)}`;
    const stationDetailsList = selectedPortfolioFaultStations
      .map(stationId => portfolioFaultDetails.stationDetails?.find(s => s.name === stationId))
      .filter(Boolean);
    const portfolioStationsList = stationDetailsList.length > 0
      ? stationDetailsList.map(station => `<span class="station-badge">${station.description}</span>`).join('')
      : '<span style="color: #999;">No stations available</span>';

    return (
      <Paper className="portfolioFaultHistogram">
        <Typography className="portfolioFaultTitle" variant="h6" color="primary">
          Fault Distribution Histogram
        </Typography>
        <PortfolioFaultSummary
          portfolioLevelText={portfolioLevelText}
          selectedPortfolioFaultStations={selectedPortfolioFaultStations}
          selectedYear={selectedYear}
          portfolioStationsList={portfolioStationsList}
          portfolioFaultData={portfolioFaultData}

        />
        <div style={{ width: '100%', height: '55vh' }}>
          {!loading && hasData && selectedPortfolioFaultStations.length > 0 ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={this.state.chartOptions}
              immutable={true}
              containerProps={{ style: { width: '100%', height: '100%' } }}
            />
          ) : (
            <Typography variant="subtitle1" style={{ margin: '20px' }}>
              {selectedPortfolioFaultStations.length === 0
                ? 'Please select at least one station to view the chart.'
                : 'Data not available. Please select filters to see the chart.'}
            </Typography>
          )}
        </div>
      </Paper>
    );
  }
}

const mapStateToProps = (state) => ({
  portfolioFaultData: state.portfolioFaultOverview?.portfolioFaultQueryData?.portfolioFaultData || [],
  portfolioFaultLogData: state.portfolioFaultOverview?.portfolioFaultLogData || [],
  selectedPortfolioFaultLevel: state.portfolioFaultOverview?.selectedPortfolioFaultLevel || 'all',
  portfolioFaultDetails: state.portfolioFaultOverview?.portfolioFaultDetails || {},
  selectedDisplayMode: state.portfolioFaultOverview?.selectedPortfolioFaultDisplayMode,
  selectedYear: state.portfolioFaultOverview?.selectedPortfolioFaultYear,
  selectedMonth: state.portfolioFaultOverview?.selectedPortfolioFaultMonth,
  selectedPortfolioFaultStations: state.portfolioFaultOverview?.selectedPortfolioFaultStations || []
});

export default connect(mapStateToProps)(PortfolioFaultHistogram);
